import React from 'react';

import WishlistButton from '@src/stories/blocks/Wishlist/WishlistButton';

import { useEnhancedBlockEvents } from '@src/hooks/useEnhancedBlockEvents';
import { useResponsiveBreakpoints } from '@src/hooks/useMediaQuery';

import { ILiquidProduct } from '@src/types/product';
import useCurrency from '@src/hooks/useCurrency';

interface ProductAddToCartWishlistProps {
  product: ILiquidProduct;
  settings?: any;
}

export const ProductAddToCartWishlist = ({ product, settings }: ProductAddToCartWishlistProps) => {
  const {
    button_text,
    button_style,
    button_size,
    button_classes,
    show_price,
    button_width_enable_responsive,
    button_width,
    button_width_mobile,
    button_width_tablet,
    button_width_ultrawide
  } = settings;
  const { isMobile, isTablet, isDesktop, isUltrawide } = useResponsiveBreakpoints();
  const { currentCurrencySymbol } = useCurrency();

  const events = useEnhancedBlockEvents(settings, button_text || 'button', 'button');

  // Check if current breakpoint has actual width value
  const hasWidthSettings = () => {
    if (button_width) return true;
    if (!button_width_enable_responsive) return false;

    if (isMobile && button_width_mobile) return true;
    if (isTablet && button_width_tablet) return true;
    if (isDesktop && button_width) return true;
    if (isUltrawide && button_width_ultrawide) return true;

    return false;
  };

  // Enhanced click handler with actions
  const handleButtonClick = (e: React.MouseEvent) => {
    // Trigger configured actions
    events.handleClick({
      buttonText: button_text || 'button',
      buttonUrl: '',
      buttonStyle: '',
      clickTarget: e?.target
    });
  };

  return (
    <div
      className={`add-to-cart-wishlist inline-flex justify-between items-stretch gap-[1px] p-0 rounded-[var(--corner-radius)] overflow-hidden ${
        hasWidthSettings() ? 'w-full' : ''
      } ${button_classes}`}
    >
      <button
        className={`flex-1 px-8 button button-${button_style} button-${button_size} rounded-none`}
        onClick={handleButtonClick}
      >
        {events.isLoading ? (
          <>
            <div className='animate-spin -ml-1 mr-2 h-4 w-4 border-2 border-current border-t-transparent rounded-full'></div>
            <span className='button__label'>Loading...</span>
          </>
        ) : (
          <div className={show_price ? 'flex items-center justify-between w-full' : ''}>
            <span className='button__label'>{button_text || 'Add to cart'}</span>
            {show_price && (
              <span>
                {currentCurrencySymbol}
                {(product.price / 100).toFixed(2)}
              </span>
            )}
          </div>
        )}
      </button>
      <div
        className={`flex items-center justify-center px-0 button button-${button_style} button-${button_size} rounded-none`}
      >
        <WishlistButton
          product={product}
          settings={settings}
          iconSize={24}
          type='icon'
        />
      </div>
    </div>
  );
};
